import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { checkoutMutations, checkoutQueryKeys, customerMutations } from '@as-react/common-queries';
import { DeliveryOptionsEnum, ErrorMessage, Provider, RoutePathname, } from '@as-react/common-types';
import { isBasketNotInitialized, mergeObjectDeep } from '@as-react/common-utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ButtonAlpha, CheckoutLayout, Grid } from '@yonderland/andes-react';
import { isAxiosError } from 'axios';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import * as Yup from 'yup';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { GUEST_USER_CHECKOUT_DATA } from '../../../constants/StorageKeysConstants';
import { useCheckoutConfig } from '../../../context/CheckoutContext';
import { useDeliveryState } from '../../../context/CheckoutDeliveryContext';
import { isUkCountry } from '../../../util/BuyFormUtil';
import CustomerUtil from '../../../util/CustomerUtil';
import SessionStorageUtil from '../../../util/SessionStorageUtil';
import { generatePathUrl } from '../../../util/URLParamUtil';
import { getAddressValidationSchema, hasPrefilledAddress } from '../../form/address-fields/addressFields.utils';
import { useScrollToFirstFormError } from '../../form/hooks/useScrollToFirstFormError';
import { getInvoiceValidationSchema } from '../../form/invoice-fields/invoiceFields.utils';
import { getCustomerDetailsValidationSchema, getPhoneNumbersValidationSchema, } from '../../form/profile-fields/CustomerDetailsFields.utils';
import { createDeliveryForm, getDeliveryAddressValidationSchema, getInitialValues, getPreselectedAddress, initialValuesDeliveryForm, } from './DeliveryForm.utils';
export const DeliveryForm = ({ children }) => {
    const { t } = useTranslation([I18N_NAMESPACES.account, I18N_NAMESPACES.checkout, I18N_NAMESPACES.general]);
    const { defaultRequestParams, servicesEndpoint, enableFullVatForm, isInAuthorMode, root } = useAemConfig();
    const history = useHistory();
    const [{ info, deliveryCountryId, activeDeliveryOption, homeDelivery, collectStore }, dispatch] = useDeliveryState();
    const { deliveryForm: { hideInvoice, showMobilePhoneNumber: showPhoneNumberAEMCheckbox }, } = useCheckoutConfig();
    const isProviderPostNL = (activeDeliveryOption === null || activeDeliveryOption === void 0 ? void 0 : activeDeliveryOption.provider) === Provider.POSTNL;
    const showMobilePhoneNumber = Boolean(showPhoneNumberAEMCheckbox && (activeDeliveryOption === null || activeDeliveryOption === void 0 ? void 0 : activeDeliveryOption.showPhoneNumber));
    const queryClient = useQueryClient();
    const { postDeliveryOption, postDelivery } = checkoutMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { updatePhoneNumber } = customerMutations({ servicesEndpoint, defaultRequestParams });
    const { mutate: updatePhoneNumberMutation } = useMutation(updatePhoneNumber());
    const { mutateAsync: postDeliveryOptionMutation } = useMutation(postDeliveryOption);
    const { mutate: postDeliveryMutation, isPending, isError } = useMutation(postDelivery);
    const isInvoiceEnabled = CustomerUtil.isInvoiceEnabled(hideInvoice, info);
    const toggleFieldName = enableFullVatForm ? 'needInvoice' : 'alternateInvoiceAddress';
    const validationSchema = Yup.object({
        chosenDeliveryOption: Yup.string(),
        customer: isProviderPostNL
            ? getCustomerDetailsValidationSchema({ t })
                .concat(getAddressValidationSchema({ t, country: info.country, prefix: 'address.' }))
                .when('chosenDeliveryOption', {
                is: (value) => !CustomerUtil.isDeliveredToDeliveryAddress(value, info),
                then: schema => schema,
                otherwise: schema => schema.strip(),
            })
            : getCustomerDetailsValidationSchema({ t }).when('chosenDeliveryOption', {
                is: (value) => !CustomerUtil.isDeliveredToDeliveryAddress(value, info),
                then: schema => schema,
                otherwise: schema => schema.strip(),
            }),
        delivery: Yup.object({
            address: getDeliveryAddressValidationSchema({ t, country: info.country }),
            saveAddress: Yup.boolean(),
        })
            .concat(getPhoneNumbersValidationSchema({ t, shouldValidate: showMobilePhoneNumber }))
            .when('chosenDeliveryOption', {
            is: (value) => CustomerUtil.isDeliveredToDeliveryAddress(value, info),
            then: schema => schema,
            otherwise: schema => schema.strip(),
        }),
        needInvoice: Yup.boolean(),
        alternateInvoiceAddress: Yup.boolean(),
        invoice: Yup.object().when([toggleFieldName, 'chosenDeliveryOption'], {
            is: (value, chosenDeliveryOption) => value &&
                isInvoiceEnabled &&
                (CustomerUtil.isDeliveredToDeliveryAddress(chosenDeliveryOption, info) ||
                    chosenDeliveryOption === DeliveryOptionsEnum.PICKUP_POINT),
            then: () => getInvoiceValidationSchema({
                t,
                country: info.country,
                fullVatFormEnabled: enableFullVatForm,
            }),
        }),
    });
    const setFormValues = (newValues, idCountry, address) => getInitialValues(newValues, mergeObjectDeep(initialValuesDeliveryForm, {
        delivery: { address: { idCountry: idCountry } },
        customer: { address: { idCountry: idCountry } },
        invoice: { address: { idCountry: idCountry } },
        idCountry: idCountry,
    }), address, activeDeliveryOption === null || activeDeliveryOption === void 0 ? void 0 : activeDeliveryOption.name, idCountry);
    const formProps = useForm({
        defaultValues: () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const data = queryClient.getQueryData(checkoutQueryKeys.getDeliveryInfo());
            const idCountry = (data === null || data === void 0 ? void 0 : data.idCountry) || deliveryCountryId;
            return setFormValues(data, idCountry, idCountry === ((_a = homeDelivery.selectedAddress) === null || _a === void 0 ? void 0 : _a.idCountry) ? homeDelivery.selectedAddress : undefined);
        }),
        mode: 'onTouched',
        shouldFocusError: true,
        resolver: useMemo(() => validationSchema && yupResolver(validationSchema), [validationSchema]),
    });
    useEffect(() => {
        const updateDeliveryAddressValues = () => __awaiter(void 0, void 0, void 0, function* () {
            const formValues = formProps.getValues();
            if (!formValues.delivery) {
                return;
            }
            const address = getPreselectedAddress(Object.assign(Object.assign({}, info), { idCountry: deliveryCountryId }), homeDelivery.selectedAddress);
            const newValues = Object.assign(Object.assign({}, formValues), { delivery: Object.assign(Object.assign({}, formValues.delivery), { address: Object.assign(Object.assign({}, formValues.delivery.address), address) }) });
            formProps.reset(newValues);
            if (homeDelivery.selectedAddress) {
                yield formProps.trigger('delivery.address');
            }
        });
        updateDeliveryAddressValues();
    }, [homeDelivery.selectedAddress]);
    useEffect(() => {
        const updateFormValues = () => __awaiter(void 0, void 0, void 0, function* () {
            const address = getPreselectedAddress(Object.assign(Object.assign({}, info), { idCountry: info.idCountry }), homeDelivery.selectedAddress);
            const newValues = setFormValues(info, info.idCountry, info.idCountry === (address === null || address === void 0 ? void 0 : address.idCountry) ? address : undefined);
            formProps.reset(newValues);
            if (hasPrefilledAddress(address)) {
                yield formProps.trigger('delivery.address');
            }
        });
        if (activeDeliveryOption && CustomerUtil.isDeliveredToDeliveryAddress(activeDeliveryOption.name, info)) {
            updateFormValues();
        }
    }, [info.idCountry, info.requiresDelivery, activeDeliveryOption === null || activeDeliveryOption === void 0 ? void 0 : activeDeliveryOption.name]);
    useScrollToFirstFormError(formProps.formState, true);
    useEffect(() => {
        if (isError) {
            dispatch({ type: 'set_delivery_error', payload: ErrorMessage.API_ERROR });
        }
    }, [isError]);
    if (!info) {
        return null;
    }
    const onSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        SessionStorageUtil.removeItem(GUEST_USER_CHECKOUT_DATA);
        info.chosenDeliveryOption = activeDeliveryOption ? activeDeliveryOption.name : info.chosenDeliveryOption;
        const deliveryForm = createDeliveryForm(info, values);
        switch (info.chosenDeliveryOption) {
            case DeliveryOptionsEnum.HOME_DELIVERY:
            case DeliveryOptionsEnum.NAMED_DAY:
            case DeliveryOptionsEnum.NEXT_DAY:
                deliveryForm.updateHomeDeliveryData(values, homeDelivery.selectedAddress).updateInvoiceData(values);
                if (showMobilePhoneNumber) {
                    deliveryForm.updatePhoneNumberData(values);
                }
                break;
            case DeliveryOptionsEnum.COLLECT_STORE:
                deliveryForm.updateCollectStoreData(values, collectStore.selectedStore);
                break;
            case DeliveryOptionsEnum.PICKUP_POINT:
                deliveryForm.updatePickupPointData(values, isProviderPostNL).updateInvoiceData(values);
                break;
            default:
                deliveryForm.updateCustomerDeliveryData(values);
                break;
        }
        if (info.chosenDeliveryOption) {
            try {
                yield postDeliveryOptionMutation(info.chosenDeliveryOption);
            }
            catch (error) {
                if (isAxiosError(error) && isBasketNotInitialized(error)) {
                    location.href = `${root}checkout/basket-overview.html`;
                }
            }
        }
        if (showMobilePhoneNumber && ((_a = values.delivery) === null || _a === void 0 ? void 0 : _a.savePhoneNumber) && ((_b = deliveryForm.data.deliveryAddress) === null || _b === void 0 ? void 0 : _b.phoneNumber)) {
            updatePhoneNumberMutation(deliveryForm.data.deliveryAddress.phoneNumber);
        }
        postDeliveryMutation(deliveryForm.data, {
            onSuccess: () => {
                queryClient.refetchQueries({
                    queryKey: checkoutQueryKeys.getPaymentInfo(),
                    stale: false,
                });
                history.push(generatePathUrl(root, `buy.html/${RoutePathname.PAYMENT}`, isInAuthorMode));
            },
            onError: error => {
                var _a;
                if (isAxiosError(error)) {
                    if (isBasketNotInitialized(error)) {
                        location.href = `${root}checkout/basket-overview.html`;
                        return;
                    }
                    if ((_a = error.response) === null || _a === void 0 ? void 0 : _a.data.find((item) => item.code === 'non_numeric_field')) {
                        dispatch({ type: 'set_delivery_error', payload: ErrorMessage.INVALID_POSTAL_CODE });
                    }
                    else {
                        if (isUkCountry(deliveryForm.data.country)) {
                            dispatch({ type: 'set_delivery_error', payload: ErrorMessage.GENERAL_ADDRESS_BUY });
                        }
                        else {
                            dispatch({ type: 'set_delivery_error', payload: ErrorMessage.API_ERROR });
                        }
                    }
                }
            },
        });
    });
    const getButtonState = () => {
        if (isPending) {
            return 'loading';
        }
        if (!activeDeliveryOption && info.requiresDelivery) {
            return 'disabled';
        }
        return 'interactive';
    };
    return (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsxs("form", { noValidate: true, name: "checkout-delivery-details-form", style: { display: 'contents' }, children: [children, _jsx(CheckoutLayout.Sticky, { children: _jsx(Grid, { columns: 12, children: _jsx(Grid.Cell, { span: { xs: 12, lg: 4 }, offset: { lg: 9 }, children: _jsx(ButtonAlpha, { fullWidth: true, state: getButtonState(), text: t(`${I18N_NAMESPACES.checkout}:checkout.delivery.submit`), theme: "branded", type: "submit", onClick: formProps.handleSubmit(onSubmit), testId: "delivery_submit" }) }) }) })] }) })));
};
